import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { COUNTRIES, CountryCode, DEFAULT_COUNTRY, SHOPIFY_RATES, getCountryConfig, isSupportedCountry } from './countries';
const COUNTRY_STORAGE_KEY = 'mpc.country';
const GEO_ENDPOINT = 'https://ipapi.co/json/';
type CurrencyContextValue = {
  country: CountryCode;
  setCountry: (code: CountryCode) => void;
  formatPrice: (amountInEur: number) => string;
  buildCartUrl: (baseUrl: string) => string;
};
const CurrencyContext = createContext<CurrencyContextValue | null>(null);
export function CurrencyProvider({
  children
}: {
  children: React.ReactNode;
}) {
  const [country, setCountryState] = useState<CountryCode>(readStoredCountry);
  useEffect(() => {
    if (hasStoredCountry()) {
      return;
    }
    let isCancelled = false;
    detectCountry().then(detected => {
      if (!isCancelled && detected) {
        setCountryState(detected);
      }
    });
    return () => {
      isCancelled = true;
    };
  }, []);
  const setCountry = useCallback((code: CountryCode) => {
    setCountryState(code);
    try {
      window.localStorage.setItem(COUNTRY_STORAGE_KEY, code);
    } catch {
      // Storage unavailable: keep the in-memory selection only.
    }
  }, []);
  const formatPrice = useCallback((amountInEur: number) => {
    const config = getCountryConfig(country);
    const rate = SHOPIFY_RATES[config.currency] ?? 1;
    const converted = amountInEur * rate;
    const step = config.roundingStep;
    const rounded = config.currency === 'EUR' ? Math.round(converted * 100) / 100 : Math.round(converted / step) * step;
    const formatted = new Intl.NumberFormat(config.locale, {
      minimumFractionDigits: config.decimals,
      maximumFractionDigits: config.decimals
    }).format(rounded);
    return config.symbolAfter ? `${formatted} ${config.symbol}` : `${config.symbol}${formatted}`;
  }, [country]);
  const buildCartUrl = useCallback((baseUrl: string) => {
    const separator = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${separator}country=${country}`;
  }, [country]);
  const value = useMemo(() => ({
    country,
    setCountry,
    formatPrice,
    buildCartUrl
  }), [country, setCountry, formatPrice, buildCartUrl]);
  return <CurrencyContext.Provider value={value} data-id="element-99" data-mp="8oxrc97i4o2g">{children}</CurrencyContext.Provider>;
}
export function useCurrency(): CurrencyContextValue {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used inside a CurrencyProvider');
  }
  return context;
}
export { COUNTRIES };
function readStoredCountry(): CountryCode {
  try {
    const stored = window.localStorage.getItem(COUNTRY_STORAGE_KEY);
    if (stored && isSupportedCountry(stored)) {
      return stored;
    }
  } catch {
    // Storage unavailable: fall back to the default country.
  }
  return DEFAULT_COUNTRY;
}
function hasStoredCountry(): boolean {
  try {
    const stored = window.localStorage.getItem(COUNTRY_STORAGE_KEY);
    return Boolean(stored && isSupportedCountry(stored));
  } catch {
    return false;
  }
}
async function detectCountry(): Promise<CountryCode | null> {
  try {
    const response = await fetch(GEO_ENDPOINT);
    if (!response.ok) {
      return null;
    }
    const data = (await response.json()) as {
      country_code?: string;
    };
    const code = data.country_code?.toUpperCase();
    if (code && isSupportedCountry(code)) {
      return code;
    }
    return null;
  } catch {
    return null;
  }
}